import { createSlice, nanoid, type PayloadAction } from "@reduxjs/toolkit";

import type { SyncRun, SyncRunDetail } from "@/types/api";

interface SyncState {
  runs: SyncRunDetail[];
}

const TODAY = "2026-05-25";

const seedRuns: SyncRunDetail[] = [
  {
    id: "run-1",
    job: "cloudbeds_pull",
    status: "acknowledged",
    started_at: `${TODAY}T02:00:00.000Z`,
    finished_at: `${TODAY}T02:03:14.000Z`,
    rows: 67,
    retry_count: 0,
    duration_ms: 194_000,
    error: null,
    request_payload: { rows: 67 },
    response_body: `{"status":"ok","received":67}`,
    events: [
      { at: `${TODAY}T02:00:00.000Z`, level: "info", message: "cloudbeds_pull started" },
      { at: `${TODAY}T02:03:14.000Z`, level: "info", message: "cloudbeds_pull completed — 67 rows" },
    ],
  },
  {
    id: "run-2",
    job: "sfm_delivery",
    status: "failed",
    started_at: `${TODAY}T03:00:00.000Z`,
    finished_at: `${TODAY}T03:00:42.000Z`,
    rows: 0,
    retry_count: 3,
    duration_ms: 42_000,
    error: "SFM Import Gateway returned 503 Service Unavailable",
    request_payload: { rows: 0 },
    response_body: `{"error":"Service Unavailable","retry_after":300}`,
    events: [
      { at: `${TODAY}T03:00:00.000Z`, level: "info", message: "sfm_delivery started" },
      { at: `${TODAY}T03:00:42.000Z`, level: "error", message: "SFM Import Gateway returned 503 Service Unavailable" },
    ],
  },
  {
    id: "run-3",
    job: "master_data_sync",
    status: "acknowledged",
    started_at: `${TODAY}T03:30:00.000Z`,
    finished_at: `${TODAY}T03:34:50.000Z`,
    rows: 12,
    retry_count: 0,
    duration_ms: 290_000,
    error: null,
    request_payload: { rows: 12 },
    response_body: `{"status":"ok","received":12}`,
    events: [
      { at: `${TODAY}T03:30:00.000Z`, level: "info", message: "master_data_sync started" },
      { at: `${TODAY}T03:34:50.000Z`, level: "info", message: "master_data_sync completed — 12 rows" },
    ],
  },
];

const initialState: SyncState = { runs: seedRuns };

const slice = createSlice({
  name: "sync",
  initialState,
  reducers: {
    replayRun(state, action: PayloadAction<string>) {
      const run = state.runs.find((r) => r.id === action.payload);
      if (!run) return;
      const finishedAt = new Date().toISOString();
      run.status = "acknowledged";
      run.finished_at = finishedAt;
      run.retry_count += 1;
      run.duration_ms = 1_800;
      run.error = null;
      run.response_body = `{"status":"ok","replay":true}`;
      run.events.push({
        at: finishedAt,
        level: "info",
        message: `Replay successful — run ${run.id}`,
      });
    },
    addRun(state, action: PayloadAction<Omit<SyncRunDetail, "id" | "events">>) {
      const id = `run-${nanoid(6)}`;
      const startedAt = action.payload.started_at;
      state.runs.unshift({
        ...action.payload,
        id,
        events: [{ at: startedAt, level: "info", message: `${action.payload.job} scheduled` }],
      });
    },
    deleteRun(state, action: PayloadAction<string>) {
      state.runs = state.runs.filter((r) => r.id !== action.payload);
    },
  },
});

export const { replayRun, addRun, deleteRun } = slice.actions;
export type { SyncRun };
export default slice.reducer;
